import { Deserializable } from '../protocols/deserializable';
import { NameChangeSubmission } from './name-change-submission';
import { SubmissionComment } from './submission-comment';
import { SubmissionTypeEnum } from '../users/enum/submission-type-enum';
import { SubmissionAttachment } from './submission-attachment';
import { LookAheadItem } from '../../views/shared/components/search-with-look-ahead/look-ahead-list/look-ahead-item/protocol/look-ahead-item';
import { Searchable } from '../protocols/searchable';
import { SubmissionSubStatusEnum } from '../enum/shared/submission-sub-status.enum';
import { AddressChangeSubmission } from './address-change-submission';
import { PhoneNumberChangeSubmission } from './phone-number-change-submission';
import { SubmissionHistoryItem } from './submission-history-item';
import { Mentionable } from '../base/mentionable';
import { PersonalDetailsSubmission } from './personal-details-submission';
import { PortalTypeIdEnum } from '../enum/shared/portal-type-id.enum';
import * as moment from 'moment/moment';
import { SubmissionStatusPill } from '../shared/submission-status-pill';
import { SubmissionStatusIdEnum } from '../enum/shared/submission-status-id.enum';
import { InvestmentInstructionSubmission } from './investment-instruction-submission';
import { RelationshipStatusSubmission } from './relationship-status-submission';
import { BeneficiaryChangeSubmission } from './beneficiary-change-submission';
import { BankingInformationSubmission } from './banking-information-submission';
import { SubmissionSubStatusIdEnum } from '../enum/shared/submission-sub-status-id-enum';
import { UpdateLegalFiduciarySubmission } from './update-legal-fiduciary-submission';
import { SelfEnrolmentSubmission } from './self-enrolment-submission';
import { VariableBenefitConfigurationSubmission } from './variable-benefit-configuration-submission';
import { MemberSubmissionStatus } from './member-submission-status';
import { UpdateVariableBenefitConfigurationSubmission } from './update-variable-benefit-configuration-submission';
import { VariableBenefitPaymentChangeSubmission } from './variable-benefit-payment-change-submission';
import { AnnuitySetupSubmission } from './annuity-setup-submission';
import { TransferInSubmission } from './transfer-in-submission';

export class MemberSubmission implements Deserializable {
  // TODO: Tidy this up once all submission types are added to this object
  public id!: number;
  public memberId!: number;
  public memberName!: string;
  public submissionTypeId!: SubmissionTypeEnum;
  public submissionTypeName!: string;
  public statusId!: SubmissionStatusIdEnum;
  public status!: MemberSubmissionStatus;
  public subStatusId!: SubmissionSubStatusIdEnum | null;
  public subStatus!: SubmissionSubStatusEnum;
  public comments!: SubmissionComment[];
  public owner!: SubmissionStakeholder;
  public ownerId!: number | null;
  public approver!: SubmissionStakeholder;
  public approverId!: number | null;
  public attachments!: SubmissionAttachment[];
  public history!: SubmissionHistoryItem[];
  public idPrefix!: string;
  public idSuffix!: string;
  public submittedDate!: string;
  public overdueDate!: string;
  public followUpDate!: string | null;
  public nameChangeSubmissionId!: string | null;
  public addressChangeSubmissionId!: string | null;
  public phoneNumberSubmissionId!: string | null;
  public personalDetailsSubmissionId!: string | null;
  public investmentInstructionsSubmissionId!: string | null;
  public relationshipStatusSubmissionId!: string | null;
  public bankingInformationSubmissionId!: string | null;
  public legalFiduciarySubmissionId!: string | null;
  public selfEnrolmentSubmissionId!: string | null;
  public variableBenefitConfigurationSubmissionId!: string | null;
  public updateVariableBenefitConfigurationSubmissionId!: string | null;
  public annuitySetupSubmissionId!: string | null;
  public transferInSubmissionId!: string | null;
  public nameChangeSubmission!: NameChangeSubmission | null;
  public phoneNumberSubmission!: PhoneNumberChangeSubmission | null;
  public addressChangeSubmission!: AddressChangeSubmission | null;
  public personalDetailsSubmission!: PersonalDetailsSubmission | null;
  public investmentInstructionSubmission!: InvestmentInstructionSubmission | null;
  public relationshipStatusSubmission!: RelationshipStatusSubmission | null;
  public beneficiaryChangeSubmission!: BeneficiaryChangeSubmission | null;
  public bankingInformationSubmission!: BankingInformationSubmission | null;
  public legalFiduciarySubmission!: UpdateLegalFiduciarySubmission | null;
  public selfEnrolmentSubmission!: SelfEnrolmentSubmission | null;
  public variableBenefitConfigurationSubmission!: VariableBenefitConfigurationSubmission | null;
  public updateVariableBenefitConfigurationSubmission!: UpdateVariableBenefitConfigurationSubmission | null;
  public variableBenefitPaymentChangeSubmission!: VariableBenefitPaymentChangeSubmission | null;
  public annuitySetupSubmission!: AnnuitySetupSubmission | null;
  public transferInSubmission!: TransferInSubmission | null;
  public reminder!: SubmissionReminder | null;
  public expirationDate!: string | null;
  public lastStatusUpdateDate!: string | null;
  public createdInPortalTypeId!: PortalTypeIdEnum;
  public completedDate!: string | null;
  public createdDate!: string | null;

  // Not From API (yet)
  public submissionId!: string;
  public formattedCreatedDate!: string;
  public formattedCompletedDate!: string;
  public formattedExpirationDate!: string;
  public statusPill!: SubmissionStatusPill;
  public expiringSoon!: boolean;

  public canChangeSubmissionOwner(): boolean {
    return (
      this.subStatusId === SubmissionSubStatusIdEnum.Ready ||
      this.subStatusId === SubmissionSubStatusIdEnum.PendingDocuments ||
      this.subStatusId === SubmissionSubStatusIdEnum.Processing ||
      this.subStatusId === SubmissionSubStatusIdEnum.MemberActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.EmployerActionRequired ||
      this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval
    );
  }

  public canChangeSubmissionApprover(): boolean {
    return this.subStatusId === SubmissionSubStatusIdEnum.ReadyForApproval;
  }

  public canUploadFiles(): boolean {
    return this.statusId === SubmissionStatusIdEnum.Submitted || this.statusId === SubmissionStatusIdEnum.InReview;
  }

  public isApproved(): boolean {
    return [SubmissionSubStatusIdEnum.Approved, SubmissionSubStatusIdEnum.AutoApproved].contains(
      this.subStatusId ?? (0 as SubmissionSubStatusIdEnum)
    );
  }

  onDeserialize(): void {
    this.submissionId = this.buildSubmissionId();
    const Deserialize = window?.injector?.Deserialize;
    if (!!this.owner) {
      this.owner = Deserialize?.instanceOf(SubmissionStakeholder, this.owner);
    }
    if (!!this.approver) {
      this.approver = Deserialize?.instanceOf(SubmissionStakeholder, this.approver);
    }
    if (!!this.comments) {
      this.comments = Deserialize?.arrayOf(SubmissionComment, this.comments);
    }
    if (!!this.reminder) {
      this.reminder = Deserialize?.instanceOf(SubmissionReminder, this.reminder);
    }
    if (!!this.history) {
      this.history = Deserialize?.arrayOf(SubmissionHistoryItem, this.history);
      this.history.sort((a, b) => new Date(b.eventDate ?? '').getTime() - new Date(a.eventDate ?? '').getTime());
    }
    if (!!this.expirationDate) {
      this.formattedExpirationDate = moment(this.expirationDate).format('MMMM D, YYYY');
      this.expiringSoon = moment(this.expirationDate).diff(moment(), 'days') <= 10;
    }
    if (!!this.createdDate) {
      this.formattedCreatedDate = moment(this.createdDate).format('MMMM D, YYYY');
    }
    if (!!this.completedDate) {
      this.formattedCompletedDate = moment(this.completedDate).format('DD / MM / YYYY');
    }
    if (!!this.status) {
      this.statusPill = new SubmissionStatusPill(this.id, this.status?.name, this.statusId, this.status?.name);
    }

    if (!!this.attachments) {
      this.attachments = Deserialize?.arrayOf(SubmissionAttachment, this.attachments);
    }
    if (!!this.addressChangeSubmission) {
      this.addressChangeSubmission = Deserialize?.instanceOf(AddressChangeSubmission, this.addressChangeSubmission);
    }
    if (!!this.phoneNumberSubmission) {
      this.phoneNumberSubmission = Deserialize?.instanceOf(PhoneNumberChangeSubmission, this.phoneNumberSubmission);
    }
    if (!!this.personalDetailsSubmission) {
      this.personalDetailsSubmission = Deserialize?.instanceOf(
        PersonalDetailsSubmission,
        this.personalDetailsSubmission
      );
    }
    if (!!this.investmentInstructionSubmission) {
      this.investmentInstructionSubmission = Deserialize?.instanceOf(
        InvestmentInstructionSubmission,
        this.investmentInstructionSubmission
      );
    }
    if (!!this.relationshipStatusSubmission) {
      this.relationshipStatusSubmission = Deserialize?.instanceOf(
        RelationshipStatusSubmission,
        this.relationshipStatusSubmission
      );
    }
    if (!!this.beneficiaryChangeSubmission) {
      this.beneficiaryChangeSubmission = Deserialize?.instanceOf(
        BeneficiaryChangeSubmission,
        this.beneficiaryChangeSubmission
      );
    }
    if (!!this.bankingInformationSubmission) {
      this.bankingInformationSubmission = Deserialize?.instanceOf(
        BankingInformationSubmission,
        this.bankingInformationSubmission
      );
    }
    if (!!this.legalFiduciarySubmission) {
      this.legalFiduciarySubmission = Deserialize?.instanceOf(
        UpdateLegalFiduciarySubmission,
        this.legalFiduciarySubmission
      );
    }
    if (!!this.selfEnrolmentSubmission) {
      this.selfEnrolmentSubmission = Deserialize?.instanceOf(SelfEnrolmentSubmission, this.selfEnrolmentSubmission);
    }
    if (!!this.variableBenefitConfigurationSubmission) {
      this.variableBenefitConfigurationSubmission = Deserialize?.instanceOf(
        VariableBenefitConfigurationSubmission,
        this.variableBenefitConfigurationSubmission
      );
    }
    if (!!this.updateVariableBenefitConfigurationSubmission) {
      this.updateVariableBenefitConfigurationSubmission = Deserialize?.instanceOf(
        UpdateVariableBenefitConfigurationSubmission,
        this.updateVariableBenefitConfigurationSubmission
      );
    }
    if (!!this.variableBenefitPaymentChangeSubmission) {
      this.variableBenefitPaymentChangeSubmission = Deserialize?.instanceOf(
        VariableBenefitPaymentChangeSubmission,
        this.variableBenefitPaymentChangeSubmission
      );
    }
    if (!!this.annuitySetupSubmission) {
      this.annuitySetupSubmission = Deserialize?.instanceOf(AnnuitySetupSubmission, this.annuitySetupSubmission);
    }
    if (!!this.transferInSubmission) {
      this.transferInSubmission = Deserialize?.instanceOf(TransferInSubmission, this.transferInSubmission);
    }
  }

  private buildSubmissionId(): string {
    // Submission IDs should be 8 characters long, padded with leading zeros
    const paddedId = this.id.toString().padStart(8, '0');
    return `${this.idPrefix}-${paddedId}-${this.idSuffix}`;
  }
}

export class SubmissionStakeholder extends Mentionable implements LookAheadItem, Searchable {
  // Searchable
  public title!: string;
  public value: any;

  onDeserialize() {
    super.onDeserialize();
    this.title = this.fullName;
    this.value = this.id;
  }

  lookAheadDisabled(): boolean {
    return false;
  }

  lookAheadDisabledMessage(): string {
    return '';
  }

  lookAheadDisplayValue(): string {
    return `${this.firstName} - ${this.lastName}`;
  }
}

export class SubmissionReminder implements Deserializable {
  public id!: number;
  public internalUserId!: number;
  public date!: string;

  onDeserialize() {}
}
